import { useState, type ReactNode } from "react";
import { Link, useNavigate , useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useVenue, initialFor } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { getRecords } from "../lib/api";
import { useCart } from "../lib/cart";
import { MailingListPopup } from "./mailing-list-popup";

// Website chrome for record-shop storefronts in a browser (see isNativeApp).
// Top nav (logo + genre nav + search + cart + account), wide document-flow
// content, and a footer, styled from the venue's own branding tokens.
export function RecordWebLayout({ children }: { children: ReactNode }) {
  // Admin pages escape the centred site container (full-width admin chrome).
  const rwPath = useRouterState({ select: (st) => st.location.pathname });
  const rwIsAdmin = rwPath === "/admin" || rwPath.startsWith("/admin/");
  const { data } = useVenue();
  const venue = data?.venue;
  const venueId = venue?.id;
  const venueName = venue?.appName || venue?.name || "";
  const logoSrc = venue?.appIconUrl || venue?.imageUrl || null;
  const { user } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const rsCfg = venue?.appConfig?.recordShop;
  // Ticker messages are admin-editable per venue, no baked-in fallbacks so
  // a non-UK shop can't accidentally advertise "Free UK shipping over £65"
  // (audit F6, 2026-09-08). Empty config → ticker bar hides entirely.
  const tickerMessages = (rsCfg?.tickerMessages ?? []).filter((m) => m.trim());

  // Top genres for the browse nav (facets come free with any records call).
  const facetsQuery = useQuery({
    queryKey: ["records-facets", venueId],
    queryFn: () => getRecords(venueId!, { limit: 1 }),
    enabled: !!venueId,
    staleTime: 5 * 60_000,
  });
  const genres = (facetsQuery.data?.facets.genres ?? []).slice(0, 8);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    navigate({ to: "/", search: q.trim() ? { q: q.trim() } : {} } as never);
  }

  const purchasesPaused = venue?.appConfig?.recordShop?.purchasesPaused === true;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[var(--background)]">
      {/* Shop paused (admin toggle): browsing stays on, buying is off, say so
          up front rather than surprising shoppers at checkout. */}
      {purchasesPaused && (
        <div className="bg-amber-500/15 py-2 text-center text-xs font-semibold text-[var(--foreground)]">
          We're not taking online orders right now, browse away, and check back soon.
        </div>
      )}
      {/* Scrolling promo ticker (messages admin-editable via Storefront
          settings). Hidden entirely when the admin hasn't configured any. */}
      {tickerMessages.length > 0 && (
        <div className="rb-marquee bg-[var(--accent)] py-1.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--accent-fg)" }} aria-hidden>
          {[0, 1].map((t) => (
            <div key={t} className="rb-marquee__track">
              {tickerMessages.map((m, i) => (
                <span key={i} className="mx-6 inline-flex items-center gap-2">
                  <span aria-hidden className="opacity-60">◆</span>
                  {m}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-[var(--border-default)] bg-[var(--background)]/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-bold text-[var(--foreground)]">
            {logoSrc ? (
              <img src={logoSrc} alt={venueName} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent)] text-sm" style={{ color: "var(--accent-fg)" }}>{initialFor(venueName)}</span>
            )}
            <span className="hidden sm:inline">{venueName}</span>
          </Link>

          <form onSubmit={submitSearch} className="ml-auto flex min-w-0 flex-1 justify-end sm:flex-none">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search records…"
              className="w-full max-w-xs rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-4 py-1.5 text-sm text-[var(--foreground)] placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:outline-none"
            />
          </form>

          <Link to="/cart" className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border-default)] text-[var(--foreground)] transition hover:border-[var(--border-strong)]" aria-label="Bag">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
            </svg>
            {cart.length > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-bold" style={{ color: "var(--accent-fg)" }}>{cart.length}</span>
            )}
          </Link>

          <Link to="/sell" className="hidden shrink-0 text-sm text-[var(--subtle)] transition hover:text-[var(--foreground)] sm:inline">
            Sell to us
          </Link>
          {user && (
            <Link to="/wishlist" className="hidden shrink-0 text-sm text-[var(--subtle)] transition hover:text-[var(--foreground)] sm:inline" aria-label="Wishlist">
              Wishlist
            </Link>
          )}
          <Link to={user ? "/account" : "/sign-in"} className="hidden shrink-0 text-sm text-[var(--subtle)] transition hover:text-[var(--foreground)] sm:inline">
            {user ? "Account" : "Sign in"}
          </Link>
        </div>

        {/* Genre nav */}
        {genres.length > 0 && (
          <nav className="border-t border-[var(--border-default)]">
            <div className="mx-auto flex w-full max-w-6xl gap-4 overflow-x-auto px-4 py-2 text-sm scrollbar-hide">
              <Link to="/" className="shrink-0 text-[var(--subtle)] transition hover:text-[var(--foreground)]">All</Link>
              {genres.map((g) => (
                <Link
                  key={g.value}
                  to="/"
                  search={{ genre: g.value } as never}
                  className="shrink-0 whitespace-nowrap text-[var(--subtle)] transition hover:text-[var(--foreground)]"
                >
                  {g.value}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className={rwIsAdmin ? "w-full flex-1 px-4 py-6 lg:px-0" : "mx-auto w-full max-w-6xl flex-1 px-4 py-8"}>{children}</main>

      {/* Footer */}
      <footer className="border-t border-[var(--border-default)] bg-[var(--surface)]">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-8 text-sm text-[var(--subtle)] sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold text-[var(--foreground)]">{venueName}</p>
            {rsCfg?.footerTagline && (
              <p className="mt-1 text-xs">{rsCfg.footerTagline}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
            <Link to="/" className="hover:text-[var(--foreground)]">Shop</Link>
            <Link to="/cart" className="hover:text-[var(--foreground)]">Bag</Link>
            <Link to="/sell" className="hover:text-[var(--foreground)]">Sell to us</Link>
            {venue?.website && (
              <a href={venue.website} target="_blank" rel="noreferrer" className="hover:text-[var(--foreground)]">Website</a>
            )}
            <span>© {venueName}</span>
          </div>
        </div>
      </footer>

      {/* Email-capture pop-up, web only (guarded inside the component too). */}
      <MailingListPopup />
    </div>
  );
}
